import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuthCtx } from "@/lib/auth-context";
import { useRecurring, formatBRL } from "@/lib/store";
import { Repeat, Pause, Play, Trash2, ArrowDownCircle, ArrowUpCircle, PlusCircle, CalendarClock } from "lucide-react";
import { RecorrentesSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/_app/recorrentes")({
  head: () => ({
    meta: [
      { title: "Recorrentes — Seu Bolso Tranquilo" },
      { name: "description", content: "Gerencie suas receitas e despesas recorrentes: aluguel, salário, assinaturas. Pause, retome ou exclua a qualquer momento." },
      { property: "og:title", content: "Recorrentes — Seu Bolso Tranquilo" },
      { property: "og:description", content: "Gerencie transações recorrentes do seu orçamento mensal." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://seubolsotranquilo.com/recorrentes" }],
  }),
  component: RecorrentesPage,
  pendingComponent: RecorrentesSkeleton,
});

const FREQ_LABEL: Record<string, string> = {
  daily: "Diariamente",
  weekly: "Semanalmente",
  monthly: "Mensalmente",
  yearly: "Anualmente",
};

function formatDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d + "T12:00:00").toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function RecorrentesPage() {
  const { user } = useAuthCtx();
  const { items, loading, toggleRule, removeRule } = useRecurring(user?.id);

  if (loading) return <RecorrentesSkeleton />;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold">Transações recorrentes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Aluguel, salário, assinaturas — criadas automaticamente na data certa.
          </p>
        </div>
        <Link
          to="/adicionar"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-primary text-primary-foreground font-medium text-sm shadow-glow hover:opacity-90 transition-opacity"
        >
          <PlusCircle className="size-4" />
          Nova recorrente
        </Link>
      </header>

      {items.length === 0 ? (
        <div className="rounded-3xl bg-gradient-card border border-border/40 p-12 text-center">
          <Repeat className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-display font-semibold">Nenhuma recorrência ainda</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Ao adicionar uma transação, ative <span className="text-foreground font-medium">Repetir transação</span>{" "}
            para que ela seja lançada automaticamente na frequência escolhida.
          </p>
          <Link
            to="/adicionar"
            className="inline-flex items-center gap-2 mt-5 px-4 py-2.5 rounded-2xl bg-gradient-primary text-primary-foreground font-medium text-sm shadow-glow hover:opacity-90 transition-opacity"
          >
            <PlusCircle className="size-4" />
            Criar primeira
          </Link>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((r) => {
            const isIncome = r.type === "income";
            const Icon = isIncome ? ArrowUpCircle : ArrowDownCircle;
            return (
              <div
                key={r.id}
                className="rounded-3xl bg-gradient-card border border-border/40 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className={"shrink-0 size-11 rounded-2xl flex items-center justify-center " + (isIncome ? "bg-success/15" : "bg-destructive/15")}>
                  <Icon className={"size-5 " + (isIncome ? "text-success" : "text-destructive")} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-semibold text-base truncate">
                      {r.description || r.category}
                    </p>
                    {!r.active && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        Pausada
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {r.category} · {FREQ_LABEL[r.frequency] ?? r.frequency}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                    <CalendarClock className="size-3.5" />
                    Próxima execução: <span className="text-foreground font-medium">{formatDate(r.next_run_date)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3 sm:gap-4">
                  <p className={"font-display font-bold text-lg " + (isIncome ? "text-success" : "text-destructive")}>
                    {isIncome ? "+" : "-"}
                    {formatBRL(Number(r.amount))}
                  </p>
                  <button
                    onClick={() => toggleRule(r.id, !r.active)}
                    className="size-9 rounded-xl flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors"
                    aria-label={r.active ? "Pausar" : "Retomar"}
                    title={r.active ? "Pausar" : "Retomar"}
                  >
                    {r.active ? <Pause className="size-4" /> : <Play className="size-4" />}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm("Excluir esta recorrência? Lançamentos já criados não serão removidos.")) {
                        removeRule(r.id);
                      }
                    }}
                    className="size-9 rounded-xl flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    aria-label="Excluir"
                    title="Excluir"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}